import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, History, Loader2 } from "lucide-react";
import { gameRequest } from "./gameApi";
import { GameBadge } from "./GameBadge";
import { pastWeekSummary, weekDates } from "@/lib/pastWeeks";

// Round L — past week standings, newest first. Fail-soft: on any error the section stays hidden.
export const PastWeeks = ({ prefix = "game-past-weeks" }) => {
  const [weeks, setWeeks] = useState([]);
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState(null);

  const load = async (before) => {
    setLoading(true);
    try {
      const data = await gameRequest(`/game/weeks?limit=8${before ? `&before=${before}` : ""}`);
      setWeeks((prev) => (before ? [...prev, ...(data.weeks || [])] : data.weeks || []));
      setNext(data.next_before || null);
    } catch {
      if (!before) setWeeks([]);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(null); }, []);
  if (!weeks.length) return null;

  return (
    <section className="game-standing mt-6" data-testid={prefix}>
      <h2 className="font-display text-base md:text-lg font-bold flex items-center gap-2 mb-3">
        <History size={17} className="text-tw-yellow" />Vorige weken
      </h2>
      <ul className="grid gap-[3px]">
        {weeks.map((w) => {
          const key = `${w.iso_year}-${w.iso_week}`;
          return <PastWeekRow key={key} week={w} open={openKey === key} prefix={`${prefix}-${key}`}
            onToggle={() => setOpenKey(openKey === key ? null : key)} />;
        })}
      </ul>
      {next && (
        <button type="button" onClick={() => load(next)} disabled={loading} data-testid={`${prefix}-more`}
          className="tw-press mt-3 h-10 px-4 inline-flex items-center gap-2 border border-tw text-white font-mono text-[11px] uppercase tracking-[0.15em] hover:border-tw-yellow disabled:opacity-50">
          {loading && <Loader2 size={13} className="animate-spin" />} Meer weken
        </button>
      )}
    </section>
  );
};

const PastWeekRow = ({ week, open, onToggle, prefix }) => {
  const s = pastWeekSummary(week);
  return (
    <li className="border border-tw bg-tw-black/40" data-testid={prefix}>
      <button type="button" onClick={onToggle} aria-expanded={open} data-testid={`${prefix}-toggle`}
        className="tw-press w-full flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-1 px-3 py-3 text-left text-sm hover:bg-white/5 transition-colors duration-150">
        <span className="min-w-[9rem] flex-1 break-words">
          <strong className="text-white">{s.title}</strong>
          <span className="text-tw-muted"> · {s.label}</span>
          <span className="block font-mono text-[10px] text-tw-muted mt-0.5">{weekDates(week)}</span>
        </span>
        {open ? <ChevronUp size={14} className="text-tw-muted shrink-0 md:order-last" /> : <ChevronDown size={14} className="text-tw-muted shrink-0 md:order-last" />}
        {s.nobody ? (
          <span className="text-tw-muted text-xs basis-full md:basis-auto" data-testid={`${prefix}-nobody`}>Niemand scoorde</span>
        ) : (
          <span className="flex items-center gap-1 basis-full md:basis-auto min-w-0 md:justify-end" data-testid={`${prefix}-winners`}>
            <GameBadge crown label="Winnaar" testId={`${prefix}-crown`} />
            <span className="min-w-0 break-words">{s.winners.join(", ")}</span>
            <strong className="font-mono whitespace-nowrap ml-1">{s.points}<span className="text-tw-muted text-xs font-normal ml-1">pnt</span></strong>
          </span>
        )}
      </button>
      {open && (
        <ol className="game-ranking border-t border-tw" data-testid={`${prefix}-ranking`}>
          {week.standings.map((row) => (
            <li key={row.user_id} className={row.is_me ? "is-me" : ""} data-testid={`${prefix}-row-${row.user_id}`}>
              <span className="game-position">{s.nobody ? "–" : row.position}</span>
              <span className="min-w-0 break-words">{row.name}{row.is_me && <span className="text-tw-yellow text-xs ml-2">jij</span>}</span>
              <strong className="game-score">{row.points}<span className="text-tw-muted text-xs font-normal ml-1">pnt</span></strong>
            </li>
          ))}
        </ol>
      )}
    </li>
  );
};
